import DataTable, { Column, Row } from "components/DataTable";
import GenericModal from "components/Modal";
import { useToaster } from "contexts/toasterContext";
import React, { useContext, useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import apiService from "services/apiService";
import AddHead, { AddHeadPayload, HeadType } from "./AddHead";
import { BannerContext } from "components/SideBar";

interface SalaryHead extends Row {
  id: number;
  head: string;
  type: HeadType;
  accountCode: string;
  createdOn: string;
  status: "Active" | "Inactive";
}

interface SalaryHeadApiRecord {
  id?: number;
  headType?: HeadType;
  headName?: string;
  status?: "Active" | "Inactive";
  createdOn?: string;
  account?: {
    accountCode?: string;
  };
}

interface SalaryHeadCreateResponse {
  id?: number;
  createdOn?: string;
  status?: "Active" | "Inactive";
  message?: string;
  data?: {
    message?: string;
  };
}

// Base URL for the payroll microservice; override via REACT_APP_PAYROLL_BASE_URL env var
const payrollApiBaseUrl =
  process.env.REACT_APP_PAYROLL_BASE_URL ?? "http://172.16.2.187:8080/api";



const Salary: React.FC = () => {
  const { addToast } = useToaster();
  const [salaryHeads, setSalaryHeads] = useState<SalaryHead[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddHeadOpen, setIsAddHeadOpen] = useState(false);
  const [editingHead, setEditingHead] = useState<SalaryHead | null>(null);

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-112px)] min-h-[300px]"
    : "h-[calc(100vh-80px)] min-h-[300px]";

  const formatDate = (date: Date) =>
    date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");

  useEffect(() => {
    setLoading(true);
    apiService
      .get("/api/salary-heads/list", {
        baseURL: payrollApiBaseUrl,
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        const rawData = response.data as
          | SalaryHeadApiRecord[]
          | { data?: SalaryHeadApiRecord[] }
          | undefined;
        const records = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.data)
            ? rawData.data
            : [];

        const mappedRows: SalaryHead[] = records.map((item, index) => ({
          id: item.id ?? Date.now() + index,
          head: item.headName || "-",
          type: (item.headType as HeadType) || "Income Head",
          accountCode: item.account?.accountCode || "-",
          createdOn: item.createdOn || "-",
          status: item.status || "Active",
        }));

        setSalaryHeads(mappedRows);
      })
      .catch((error) => {
        console.error("Failed to fetch salary heads:", error);
        const typedError = error as {
          response?: { data?: { message?: string; error?: string } };
          message?: string;
        };
        addToast(
          typedError?.response?.data?.message ||
          typedError?.response?.data?.error ||
          typedError?.message ||
          "Failed to fetch salary heads",
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast]);

  const handleAddHead = async (payload: AddHeadPayload) => {
    if (editingHead) {
      setSalaryHeads((prev) =>
        prev.map((item) =>
          item.id === editingHead.id
            ? {
              ...item,
              head: payload.headName,
              type: payload.headType,
              accountCode: payload.accountCode,
            }
            : item,
        ),
      );
      setEditingHead(null);
      setIsAddHeadOpen(false);
      return;
    }

    if (!payload.accountId) {
      throw new Error("Please select a valid account code");
    }

    const requestPayload = {
      headType: payload.headType,
      headName: payload.headName,
      account: {
        id: payload.accountId,
        accountCode: payload.accountCode,
      },
      status: "Active",
    };

    const response = await apiService.post("/api/salary-heads/create", requestPayload, {
      baseURL: payrollApiBaseUrl,
      headers: { "Content-Type": "application/json" },
    });

    const responseData = response.data as SalaryHeadCreateResponse | undefined;

    const nextHead: SalaryHead = {
      id: responseData?.id ?? Date.now(),
      head: payload.headName,
      type: payload.headType,
      accountCode: payload.accountCode,
      createdOn: responseData?.createdOn || formatDate(new Date()),
      status: responseData?.status || "Active",
    };

    const successMessage =
      responseData?.message ||
      responseData?.data?.message ||
      "Salary head added successfully";

    addToast(successMessage, "success");
    setSalaryHeads((prev) => [nextHead, ...prev]);
    setIsAddHeadOpen(false);
  };

  const handleEdit = (head: SalaryHead) => {
    setEditingHead(head);
    setIsAddHeadOpen(true);
  };

  const handleDelete = (head: SalaryHead) => {
    setSalaryHeads((prev) => prev.filter((item) => item.id !== head.id));
  };

  const tableColumns: Column[] = [
    { key: "head", label: "Head", bold: true },
    { key: "type", label: "Type", bold: true },
    { key: "accountCode", label: "Code", bold: true },
    { key: "createdOn", label: "Created", bold: true },

    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span className="bg-success/10 text-success text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-success/20  dark:text-success-400 border border-success/20">
          {value}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row) => {
        const head = row as SalaryHead;

        return (
          <div className="flex flex-start">
            <span
              onClick={() => handleEdit(head)}
              className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
              title="Edit"
            >
              <FaEdit className="text-white" />
            </span>
            <span
              onClick={() => handleDelete(head)}
              className="btn-red px-2 py-2 rounded-lg cursor-pointer"
              title="Delete"
            >
              <MdDelete className="text-white" />
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      {/* <BreadCrumb BreadCrumbList={breadCrumbList} /> */}

      <DataTable
        heightClass={dynamicHeightClass}
        data={salaryHeads}
        columns={tableColumns}
        loading={loading}
        onAddBtnClick={() => {
          setEditingHead(null);
          setIsAddHeadOpen(true);
        }}
      />

      {isAddHeadOpen && (
        <GenericModal
          isOpen={isAddHeadOpen}
          onClose={() => {
            setIsAddHeadOpen(false);
            setEditingHead(null);
          }}
          header={editingHead ? "Edit Head" : "Create Head"}
          customCss={{ maxWidth: "960px" }}
        >
          <div className="bg-white dark:bg-gray-800">
            <AddHead
              onClose={() => {
                setIsAddHeadOpen(false);
                setEditingHead(null);
              }}
              onSave={handleAddHead}
              initialValues={
                editingHead
                  ? {
                    headType: editingHead.type,
                    accountCode: editingHead.accountCode,
                    headName: editingHead.head,
                  }
                  : undefined
              }
            />
          </div>
        </GenericModal>
      )}
    </div>
  );
};

export default Salary;
